import { BADGE_TIERS, badgeTitleId, badgeTitleName } from './badges';
import { SCHOOLS } from './schools';

/** How a title glows over its bearer's head (the client animates each style). */
export type TitleStyle = 'gold' | 'frost' | 'flame' | 'jade' | 'blood' | 'violet' | 'rainbow' | 'ink';

/**
 * The decorated frame a title is shown in (docs/art/titles-ref/): a lacquer or paper bar edged in
 * gold with a themed ornament breaking out of it. In the reference sheet's order (01 to 20).
 */
export type TitlePlate =
  | 'martial'
  | 'master'
  | 'immortal'
  | 'warrior'
  | 'wanderer'
  | 'ascetic'
  | 'heir'
  | 'challenger'
  | 'blossom'
  | 'winter'
  | 'sky-dragon'
  | 'white-tiger'
  | 'fox'
  | 'crane'
  | 'lotus'
  | 'maple'
  | 'stream'
  | 'wilds'
  | 'falling-flowers'
  | 'moon';

/** Every plate, in the reference sheet's order. */
export const TITLE_PLATES: readonly TitlePlate[] = [
  'martial',
  'master',
  'immortal',
  'warrior',
  'wanderer',
  'ascetic',
  'heir',
  'challenger',
  'blossom',
  'winter',
  'sky-dragon',
  'white-tiger',
  'fox',
  'crane',
  'lotus',
  'maple',
  'stream',
  'wilds',
  'falling-flowers',
  'moon',
];

/** The plate a title of each style is shown in when it names none of its own. */
export const STYLE_PLATES: Readonly<Record<TitleStyle, TitlePlate>> = {
  gold: 'master',
  frost: 'winter',
  flame: 'warrior',
  jade: 'wanderer',
  blood: 'challenger',
  violet: 'ascetic',
  rainbow: 'sky-dragon',
  ink: 'heir',
};

/** What earns a title. */
export type TitleUnlock =
  | { readonly kind: 'start' }
  | { readonly kind: 'kills'; readonly monster: string; readonly count: number }
  | { readonly kind: 'quest'; readonly quest: string }
  | { readonly kind: 'school'; readonly school: string }
  | { readonly kind: 'mastery' }
  | { readonly kind: 'legend' }
  /** Held by a single martial artist in the whole world; granted, never earned by a counter. */
  | { readonly kind: 'unique' }
  /** A battle pass reward for that season. */
  | { readonly kind: 'pass'; readonly season: string }
  /** The top tier of a ladder. */
  | { readonly kind: 'ladder'; readonly ladder: string }
  /** First in the arena when a day ends. */
  | { readonly kind: 'arena' }
  /** A school badge tier, claimed in the school panel once the school's neigong is high enough. */
  | { readonly kind: 'badge'; readonly school: string; readonly tier: number };

export interface TitleDef {
  readonly id: string;
  /** Shown over the head. */
  readonly name: string;
  readonly style: TitleStyle;
  /** The frame it is shown in; its style's plate (`STYLE_PLATES`) when left out. */
  readonly plate?: TitlePlate;
  readonly unlock: TitleUnlock;
  /** How to earn it, for the title list. */
  readonly how: string;
  /** Rewards that come with the title: a mount and a fashion outfit. */
  readonly grants?: { readonly mount?: string; readonly skin?: string };
  /** An aura the bearer carries while showing this title (over any neigong aura). */
  readonly aura?: { readonly style: 'swordink'; readonly color: string };
}

const list: TitleDef[] = [
  {
    id: 'blood-oath',
    name: 'ผู้ลงนามสัญญาเลือด',
    style: 'blood',
    unlock: { kind: 'start' },
    how: 'ติดตัวตั้งแต่ลงนามสัญญาเลือด',
  },
  ...Object.values(SCHOOLS).map((sc): TitleDef => ({
    id: `disciple-${sc.id}`,
    name: sc.discipleTitle,
    style: sc.id === 'cinnabar' ? 'blood' : sc.id === 'dim-moon' ? 'violet' : 'jade',
    unlock: { kind: 'school', school: sc.id },
    how: `เข้าสำนัก${sc.name}`,
  })),
  ...Object.values(SCHOOLS).flatMap((sc) =>
    BADGE_TIERS.map((b): TitleDef => ({
      id: badgeTitleId(sc.id, b.tier),
      name: badgeTitleName(sc.id, b.tier),
      style: (['ink', 'flame', 'frost', 'gold'] as const)[b.tier - 1]!,
      unlock: { kind: 'badge', school: sc.id, tier: b.tier },
      how: `รับ${b.name}สำนัก${sc.name} (กำลังภายในสำนักขั้น ${b.level})`,
    })),
  ),
  {
    id: 'wolf-hunter',
    name: 'นักล่าหมาป่าเงาจันทร์',
    style: 'frost',
    plate: 'moon',
    unlock: { kind: 'kills', monster: 'wolf', count: 20 },
    how: 'ปราบหมาป่าเงาจันทร์ 20 ตัว',
  },
  {
    id: 'bandit-bane',
    name: 'ฝันร้ายของโจรภูเขา',
    style: 'flame',
    plate: 'maple',
    unlock: { kind: 'kills', monster: 'bandit', count: 20 },
    how: 'ปราบโจรภูเขา 20 คน',
  },
  {
    id: 'black-sword-slayer',
    name: 'ผู้พิชิตกระบี่ทมิฬ',
    style: 'gold',
    plate: 'crane',
    unlock: { kind: 'quest', quest: 'black-sword' },
    how: 'ทำภารกิจ「ปีศาจกระบี่ทมิฬ」สำเร็จ',
  },
  {
    id: 'first-step',
    name: 'ผู้ก้าวแรกบนทุ่งราบ',
    style: 'jade',
    plate: 'wanderer',
    unlock: { kind: 'quest', quest: 'plains-masks' },
    how: 'ผ่านหน้ากากไม้และหัวหน้าโจรภายในชั่วโมงแรก',
  },
  {
    id: 'neigong-master',
    name: 'ผู้บรรลุลมปราณ',
    style: 'jade',
    plate: 'lotus',
    unlock: { kind: 'mastery' },
    how: 'ฝึกกำลังภายในเล่มใดก็ได้จนตัน',
  },
  {
    id: 'legend',
    name: 'ตำนานเก้ากระบี่',
    style: 'rainbow',
    unlock: { kind: 'legend' },
    how: 'ทำภารกิจทั้งหมดสำเร็จ และฝึกกำลังภายในจนตัน',
  },
  {
    id: 'dugu',
    name: 'เถาฉางเยี่ย',
    style: 'ink',
    plate: 'martial',
    unlock: { kind: 'unique' },
    how: 'จอมกระบี่ผู้ไร้พ่าย มีได้เพียงหนึ่งเดียวในยุทธภพ · ได้รับกวางเมฆหิมะ ชุดจอมมารกระบี่ และออร่าเจตจำนงกระบี่',
    grants: { mount: 'black-eagle', skin: 'dugu-robe' },
    aura: { style: 'swordink', color: '60,60,70' },
  },
  {
    id: 's1-wanderer',
    name: 'ผู้พเนจรฤดูกาลแรก',
    style: 'jade',
    plate: 'wilds',
    unlock: { kind: 'pass', season: 's1' },
    how: 'ตั๋วยุทธภพ ฤดูกาลที่ 1 ขั้น 30 (ฟรี)',
  },
  {
    id: 's1-sovereign',
    name: 'จอมยุทธ์ศิลาเก้ากระบี่',
    style: 'rainbow',
    plate: 'white-tiger',
    unlock: { kind: 'pass', season: 's1' },
    how: 'ตั๋วยุทธภพ ฤดูกาลที่ 1 ขั้น 30 (พรีเมียม)',
  },
  {
    id: 'heaven-ladder',
    name: 'เทพแห่งบันไดสวรรค์',
    style: 'gold',
    plate: 'immortal',
    unlock: { kind: 'ladder', ladder: 'heaven' },
    how: 'ไต่บันไดสวรรค์ถึงขั้นเทพ (คะแนน 1,800)',
  },
  {
    id: 'hell-ladder',
    name: 'ราชันมารแห่งบันไดนรก',
    style: 'blood',
    plate: 'fox',
    unlock: { kind: 'ladder', ladder: 'hell' },
    how: 'ไต่บันไดนรกถึงขั้นราชันมาร (คะแนน 1,800)',
  },
  {
    id: 'arena-champion',
    name: 'จ้าวสังเวียนประลองยุทธ',
    style: 'flame',
    plate: 'falling-flowers',
    unlock: { kind: 'arena' },
    how: 'จบวันที่อันดับ 1 ของประลองยุทธ',
  },
  {
    // Granted by the top sparring tier's first win (data/sparring.ts), never by a counter.
    id: 'ring-master',
    name: 'ผู้พิชิตลานประลอง',
    style: 'gold',
    unlock: { kind: 'arena' },
    how: 'ชนะคู่ฝึกปรมาจารย์ที่ลานประลอง',
  },
  {
    // Granted by the fair's top scorer (data/worldEvents.ts `FAIR.top`); the next fair's winner takes it again.
    id: 'bun-tower-king',
    name: 'เจ้าหอซาลาเปา',
    style: 'gold',
    plate: 'martial',
    unlock: { kind: 'unique' },
    how: 'ทำคะแนนสูงสุดในงานชิงซาลาเปาบนหอไผ่',
  },
  {
    // Granted by the hidden master's test (data/hiddenMaster.ts).
    id: 'cloud-elder-pupil',
    name: 'ศิษย์นอกสำนักของผู้เฒ่าเหยียบเมฆ',
    style: 'jade',
    plate: 'crane',
    unlock: { kind: 'unique' },
    how: 'ผ่านบททดสอบของผู้เฒ่าเหยียบเมฆบนเสาหินโดดเดี่ยว',
  },
];

export const TITLES: Readonly<Record<string, TitleDef>> = Object.fromEntries(list.map((t) => [t.id, t]));
export const TITLE_LIST: readonly TitleDef[] = list;

/** The plate a title is shown in. */
export const plateOf = (t: TitleDef): TitlePlate => t.plate ?? STYLE_PLATES[t.style];

export function getTitle(id: string): TitleDef {
  const t = TITLES[id];
  if (!t) throw new Error(`Unknown title: ${id}`);
  return t;
}

/** The title a name belongs to (saves before titles had ids stored only the name). */
export const titleByName = (name: string): TitleDef | undefined => list.find((t) => t.name === name);
